import {
  COMPANY_SEARCH_API_PREFIX,
  LOG_API_CALL_DURATION_ENABLE,
} from '@aibees/elephant/constants';
import {
  BULK_FILTER_VALUE_SEPARATOR,
  COMPANY_ID_FILTER_SEPARATOR,
  FILTER_VALUE_SEPARATOR,
  RANGE_FILTER_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import {
  useSearchModeFromRoute,
  useSearchQueryFilter,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useModalityChangeCounterStore } from '@aibees/elephant/store/modality-change-counter';
import {
  SearchMatchType,
  SearchMode,
  SearchType,
} from '@aibees/elephant/types';
import {
  HttpMethod,
  Maybe,
  PaginationRequest,
} from '@aibees/shared/definition';
import { useBaseQuery } from '@aibees/shared/http';
import {
  cleanUrl,
  objSanitizeNilValues,
  peformanceLogTime,
  stableHash,
} from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { UseQueryOptions } from '@tanstack/react-query';
import { isEmpty, omit } from 'lodash-es';
import { useDeferredValue, useMemo } from 'react';
import {
  addEmployeeMetric,
  addGrowthMetric,
} from '../../search-filters/filters/employee-by-department-filter/utils';
import { parseNameIdValue } from '../../search-filters/utils';
import { COMPANY_SEARCH_API_RESPONSE_PERF_MARK } from '../config';
import {
  mappingCompanyRevenue,
  mappingCustomRange,
  mappingFollowerRanges,
} from '../range-mappers';
import {
  extractFilterSearchMatchType,
  extractSearchMatchValuesForKeywordSources,
  handleSearchMatchInclusionQuery,
} from '../utils';
import {
  CompanyRequestType,
  CompanySearchBody,
  CompanySearchResponse,
  InclusionMode,
  InclusionQueries,
  InclusionWithSourceQueries,
  JobExperienceQuery,
  Operator,
  QueryTarget,
  RangeQueries,
  RangeQuery,
  RangeType,
  SearchModality,
  SocialMediaFollowerKind,
  SocialMediaFollowerPlatform,
} from './types';
import { useUpdateCompanySearchItemsStore } from './use-update-company-search-items-store';

export type BrowseCompanySearchRequest = PaginationRequest<{
  data?: {
    type?: string;
    value?: string;
    data?: Record<string, any>;
  };
  value?: any;
}>;

/**
 * Explicit dependencies for the core hook, allowing callers to provide
 * pre-resolved values instead of subscribing to stores internally.
 */
export interface BrowseCompanySearchDeps {
  workspace?: string;
  type?: SearchType;
  mode?: SearchMode;
  searchParams: Record<string, any>;
  modalityVersion: Record<string, string>;
}

/**
 * Core hook that performs the company search query.
 * Does NOT subscribe to any stores — all dependencies come through `deps`.
 * This eliminates redundant re-renders when multiple callers share the same
 * store subscriptions (e.g. useSearchQueryFilter).
 */
export function useBrowseCompanySearchCore(
  request: BrowseCompanySearchRequest,
  deps: BrowseCompanySearchDeps,
  options?: UseQueryOptions<any, any> & { forceEnabled?: boolean },
  isNotSearchTableQuery?: boolean
) {
  const { workspace, type, mode, searchParams, modalityVersion } = deps;

  const { page, size, ...requestData } = {
    ...request,
    page: request.page || 0,
    size: request.size || 25,
  };

  const reqParams = objSanitizeNilValues(
    omit({ ...requestData.data, ...searchParams }, 'type', 'page', 'size')
  );

  // transform
  const transformedRequestParams = transform(reqParams, mode);

  const modalityVersionQueryKey = useMemo(() => {
    return modalityVersion[
      (transformedRequestParams.modality ??
        SearchModality.TOTAL) as SearchModality
    ];
  }, [transformedRequestParams, modalityVersion]);

  const queryKey = [
    'useBrowseCompanySearch',
    type,
    page,
    size,
    stableHash(omit(transformedRequestParams, 'modality')),
    modalityVersionQueryKey, // This will cause refetch on each modality change
  ];

  const isRequestEmpty = useMemo(() => {
    return isEmpty(reqParams);
  }, [reqParams]);

  const query = useBaseQuery<
    Omit<BrowseCompanySearchRequest, 'sort'> & { sort?: [string] },
    CompanySearchResponse,
    CompanySearchResponse
  >(
    queryKey,
    {
      url: COMPANY_SEARCH_API_PREFIX.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      headers: { 'x-customer-id': workspace },
      data: {
        page,
        size,
        value: transformedRequestParams.value,
        ...transformedRequestParams,
      },
    },
    {
      ...{
        ...options,
        ...{
          enabled: options?.enabled && !isRequestEmpty,
          // keepPreviousData: true,
          logTime: true,
        },
      },
      onSuccess: (data: CompanySearchResponse) => {
        performance.mark(COMPANY_SEARCH_API_RESPONSE_PERF_MARK);
        if (LOG_API_CALL_DURATION_ENABLE) {
          peformanceLogTime('useBrowseCompanySearch success');
        }
        return data;
      },
    }
  );

  useUpdateCompanySearchItemsStore(query, isNotSearchTableQuery);

  return query;
}

/**
 * Convenience wrapper that reads dependencies from stores/context.
 * Use this for standalone callers (outside the advance-table tree) that
 * don't already hold the required deps.
 *
 * For the advance-table path, prefer `useBrowseCompanySearchCore` directly
 * to avoid redundant store subscriptions.
 */
export function useBrowseCompanySearch(
  request: BrowseCompanySearchRequest,
  options?: UseQueryOptions<any, any> & { forceEnabled?: boolean },
  isNotSearchTableQuery?: boolean
) {
  const workspace = useWorkspace()?.id;
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const { counter: modalityVersion } = useModalityChangeCounterStore();

  return useBrowseCompanySearchCore(
    request,
    { workspace, type, mode, searchParams: deferredParams, modalityVersion },
    options,
    isNotSearchTableQuery
  );
}

export function addInclusionWithSourceQuery(
  request: CompanySearchBody,
  name: keyof InclusionWithSourceQueries,
  op: Operator,
  m: InclusionMode,
  target: QueryTarget,
  value: string,
  sourceValue: string,
  sourceSearchMatchValues: string
) {
  const { searchMatchValues } = extractSearchMatchValuesForKeywordSources(
    target,
    sourceValue,
    sourceSearchMatchValues
  );

  const valueArr = value.split(FILTER_VALUE_SEPARATOR);
  const draft = { content: valueArr, sources: searchMatchValues };
  if (!request[target][name]) request[target][name] = { [op]: { [m]: draft } };

  if (!request[target][name]![op]) request[target][name]![op] = { [m]: draft };
  else if (request[target][name]![op]) {
    request[target][name]![op] = {
      ...request[target][name]![op],
      ...{
        [m]: {
          ...request[target][name]![op]![m],
          ...draft,
        },
      },
    };
    return request;
  }

  if (!request[target][name]![op]![m]) request[target][name]![op]![m] = draft;
  else if (request[target][name]![op]![m]) {
    request[target][name]![op]![m] = {
      ...request[target][name]![op]![m],
      ...draft,
    };
    return request;
  }

  return request;
}

export function addInclusionQuery(
  request: CompanySearchBody,
  name: keyof InclusionQueries,
  op: Operator,
  m: InclusionMode,
  value: string,
  target: QueryTarget,
  field?: string,
  searchMatchValues?: string,
  defaultSearchMatchType?: SearchMatchType // default search match type is SMART if not provided
) {
  if (!value) return request;

  if (!request[target]) {
    request[target] = {} as InclusionQueries;
  }

  const { searchMatchType, valueDraft } = handleSearchMatchInclusionQuery(
    name,
    value,
    target,
    field,
    searchMatchValues,
    defaultSearchMatchType
  );

  if (name === 'company') {
    if (!request[target].company) {
      request[target].company = {};
    }

    (['current', 'previous'] as const).forEach((type) => {
      if (!request[target].company![type]) {
        request[target].company![type] = {};
      }
      if (!request[target].company![type]![op]) {
        request[target].company![type]![op] = {};
      }
      if (!request[target].company![type]![op]![m]) {
        request[target].company![type]![op]![m] = valueDraft;
      } else {
        request[target].company![type]![op]![m] = searchMatchType
          ? {
              mode: searchMatchType,
              content: [...value.split(FILTER_VALUE_SEPARATOR)],
            }
          : [
              ...(request[target].company![type]![op]![m] as string[]),
              ...value.split(FILTER_VALUE_SEPARATOR),
            ];
      }
    });

    return request;
  }

  if (name === 'education' || name === 'searchList') {
    return request;
  }

  if (!request[target][name]) {
    request[target][name] = {
      [op]: { [m]: valueDraft },
    };
  } else if (!request[target][name]![op]) {
    request[target][name] = {
      ...request[target][name],
      [op]: { [m]: valueDraft },
    };
  } else {
    request[target][name]![op]![m] = searchMatchType
      ? {
          mode: searchMatchType,
          content: [...value.split(FILTER_VALUE_SEPARATOR)],
        }
      : value.split(FILTER_VALUE_SEPARATOR);
  }

  return request;
}

export function addEducationInclusionQuery(
  request: CompanySearchBody,
  name: keyof InclusionQueries,
  op: Operator,
  m: InclusionMode,
  value: string,
  target: QueryTarget,
  nestedName?: string,
  field?: string,
  searchMatchValues?: string
) {
  if (!value) return request;

  const { searchMatchType } = extractFilterSearchMatchType(
    field,
    searchMatchValues,
    nestedName !== 'school'
  );

  const v = value?.split(FILTER_VALUE_SEPARATOR);
  const draftValue = searchMatchType
    ? {
        mode: searchMatchType,
        content: v,
      }
    : v;

  {
    request[target] ||= {};
    let draftField: any = request[target];

    if (nestedName) {
      draftField[name] ||= {};
      draftField = draftField[name];
      name = nestedName as any;
    }

    if (!draftField[name])
      draftField[name] = {
        [op]: { [m]: draftValue },
      };
    if (draftField[name]![op]) {
      draftField[name] = {
        ...draftField[name],
        ...{
          [op]: {
            ...draftField[name]?.[op],
            ...{ [m]: draftValue },
          },
        },
      };
    } else if (!draftField[name]![op])
      draftField[name] = {
        ...draftField[name],
        ...{ [op]: { [m]: draftValue } },
      };
    else if (!draftField[name]?.[op]?.[m]) {
      draftField[name]![op]![m] = draftValue as never;
    }
    return request;
  }
}

export function addSearchListInclusionQuery(
  request: CompanySearchBody,
  name: keyof InclusionQueries,
  op: Operator,
  m: InclusionMode,
  value: string,
  target: QueryTarget,
  nestedName?: string
) {
  if (!value) return request;

  const v = value?.split(FILTER_VALUE_SEPARATOR);

  {
    request[target] ||= {};
    let draftField: any = request[target];

    if (nestedName) {
      draftField[name] ||= {};
      draftField = draftField[name];
      name = nestedName as any;
    }

    if (!draftField[name])
      draftField[name] = {
        [op]: { [m]: v },
      };
    if (draftField[name]![op]) {
      draftField[name] = {
        ...draftField[name],
        ...{
          [op]: {
            ...draftField[name]?.[op],
            ...{ [m]: v },
          },
        },
      };
    } else if (!draftField[name]![op])
      draftField[name] = {
        ...draftField[name],
        ...{ [op]: { [m]: v } },
      };
    else if (!draftField[name]?.[op]?.[m]) {
      draftField[name]![op]![m] = v as never;
    }
    return request;
  }
}

export function addCompanyInclusionQuery(
  request: CompanySearchBody,
  type: 'current' | 'previous' | 'latest',
  op: Operator,
  m: InclusionMode,
  value?: string
) {
  if (!value) return request;

  if (!request.contact) {
    request.contact = {};
  }
  if (!request.contact.company) {
    request.contact.company = {};
  }
  if (!request.contact.company[type]) {
    request.contact.company[type] = {};
  }
  if (!request.contact.company[type]![op]) {
    request.contact.company[type]![op] = {};
  }

  const values = value.split(FILTER_VALUE_SEPARATOR);

  if (!request.contact.company[type]![op]![m]) {
    request.contact.company[type]![op]![m] = values;
  } else {
    request.contact.company[type]![op]![m] = [
      ...(request.contact.company[type]![op]![m] as string[]),
      ...values,
    ];
  }

  return request;
}

function addRangeQuery(
  request: CompanySearchBody,
  name: keyof RangeQueries,
  target: QueryTarget,
  type?: RangeType,
  key?: 'start' | 'end' | 'range',
  value?: string,
  customRangeSeparator?: string
) {
  if (type === 'ALL' || type === 'NONE') {
    request[target][name] = { type };
    return request;
  }
  let draft = (request[target][name] as RangeQuery)?.range;
  if (key === 'range') {
    if (value) {
      const ranges = value.split(FILTER_VALUE_SEPARATOR);
      draft = ranges?.map((range) => {
        const r = range.split(customRangeSeparator ?? RANGE_FILTER_SEPARATOR);
        return {
          start: r?.[0] ? Number(r?.[0]) : undefined,
          end: r?.[1] ? Number(r?.[1]) : undefined,
        };
      });
    }
  } else {
    draft = { ...draft, ...{ [key!]: value } };
  }
  request[target][name] = { type, range: draft };
  return request;
}

function addSingleRangeQuery(
  request: CompanySearchBody,
  name: keyof RangeQueries,
  target: QueryTarget,
  value?: string
) {
  const r = value?.split(RANGE_FILTER_SEPARATOR);
  const draft = {
    start: r?.[0] ? Number(r?.[0]) : undefined,
    end: r?.[1] ? Number(r?.[1]) : undefined,
  };
  request[target][name] = {
    ...request[target][name],
    ...{ range: draft, type: 'RANGE' },
  };
  return request;
}

export function addJobTitleQuery(
  experience: JobExperienceQuery,
  type: 'current' | 'previous' | 'latest',
  op: Operator,
  m: InclusionMode,
  value: string,
  field?: string,
  searchMatchValues?: string
) {
  // eslint-disable-next-line prefer-const
  let exp = experience.experience ? experience : { experience: {} };

  if (!exp.experience![type]) {
    exp.experience![type] = {};
  }

  const { searchMatchType } = extractFilterSearchMatchType(
    field,
    searchMatchValues,
    true
  );

  const v = value?.split(FILTER_VALUE_SEPARATOR);
  const draftValue = searchMatchType
    ? {
        mode: searchMatchType,
        content: v,
      }
    : v;

  if (!exp.experience![type]!.title) {
    exp.experience![type]!.title = {
      [op]: { [m]: draftValue },
    };
  }
  if (exp.experience![type]!.title![op]) {
    exp.experience![type]!.title = {
      ...exp.experience![type]!.title,
      ...{
        [op]: {
          ...exp.experience![type]!.title![op],
          ...{ [m]: draftValue },
        },
      },
    };
  } else if (!exp.experience![type]!.title![op]) {
    exp.experience![type]!.title = {
      ...exp.experience![type]?.title,
      ...{ [op]: { [m]: draftValue } },
    };
  } else if (!exp.experience![type]?.title![op]?.[m]) {
    exp.experience![type]!.title![op]![m] = draftValue as never;
  }
  return exp;
}

// as we're using separate query key for cities for now, the citiesQuery will be empty
// but we need to keep the function for future use
function calcSelectedLocations(regionsQuery?: string, citiesQuery?: string) {
  const seperatedItems = regionsQuery
    ?.split(FILTER_VALUE_SEPARATOR)
    .map((i) => i.replaceAll('::', ' '));

  const regionsResult = seperatedItems?.join(FILTER_VALUE_SEPARATOR);

  const result =
    (regionsResult ?? '') +
    (regionsResult && citiesQuery ? FILTER_VALUE_SEPARATOR : '') +
    (citiesQuery ?? '');

  return result;
}

export function setNestedRange(
  request: CompanySearchBody,
  target: QueryTarget,
  platform: SocialMediaFollowerPlatform,
  kind: SocialMediaFollowerKind,
  value: RangeQuery
) {
  const slot = request[target];
  slot.socialMediaFollower = {
    ...slot.socialMediaFollower,
    [platform]: {
      ...slot.socialMediaFollower?.[platform],
      [kind]: value,
    },
  };
  return request;
}

export function transform(r: Record<string, any>, mode: Maybe<SearchMode>) {
  let request: CompanySearchBody = {
    type:
      mode === SearchMode.Semantic
        ? CompanyRequestType.SEMANTIC
        : CompanyRequestType.SIMILARITY,
    contact: {},
    account: {},
  };

  // r = handleExactMatchInSearchRequest(r);

  if (r.value) {
    request.value = r.value.split(FILTER_VALUE_SEPARATOR);
  }

  if (r.company_search_list_id) {
    const v = r.company_search_list_id.split(COMPANY_ID_FILTER_SEPARATOR)[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'include',
      v,
      QueryTarget.account,
      'companyId'
    );
  }

  if (r.include_company_name)
    request = addInclusionQuery(
      request,
      'name',
      'any',
      'include',
      r.include_company_name,
      QueryTarget.account,
      'include_company_name',
      r.exact_match
    );

  if (r.include_company_domain)
    request = addInclusionQuery(
      request,
      'domain',
      'any',
      'include',
      r.include_company_domain,
      QueryTarget.account
    );

  if (r.include_company_name_or_domain)
    request = addInclusionQuery(
      request,
      'nameOrDomain',
      'any',
      'include',
      r.include_company_name_or_domain,
      QueryTarget.account,
      'include_company_name_or_domain',
      r.exact_match
    );

  if (r.relevancy)
    request = addInclusionQuery(
      request,
      'relevancy',
      'any',
      'include',
      r.relevancy,
      QueryTarget.account
    );

  if (r.company_type_include_any)
    request = addInclusionQuery(
      request,
      'type',
      'any',
      'include',
      r.company_type_include_any,
      QueryTarget.account
    );

  if (r.company_type_exclude)
    request = addInclusionQuery(
      request,
      'type',
      'any',
      'exclude',
      r.company_type_exclude,
      QueryTarget.account
    );

  if (r.company_type_include_and)
    request = addInclusionQuery(
      request,
      'type',
      'all',
      'include',
      r.company_type_include_and,
      QueryTarget.account
    );

  if (r.naics_include_any)
    request = addInclusionQuery(
      request,
      'naics',
      'any',
      'include',
      r.naics_include_any,
      QueryTarget.account
    );

  if (r.naics_exclude_any)
    request = addInclusionQuery(
      request,
      'naics',
      'any',
      'exclude',
      r.naics_exclude_any,
      QueryTarget.account
    );

  if (r.naics_include_and)
    request = addInclusionQuery(
      request,
      'naics',
      'all',
      'include',
      r.naics_include_and,
      QueryTarget.account
    );

  if (
    r.company_hq_include_zip_code_country &&
    r.company_hq_include_zip_code_distance
  ) {
    const geoLocation = r.company_hq_include_zip_code_country.split(':');
    request[QueryTarget.account] = {
      ...request[QueryTarget.account],
      ...{
        geoLocation: {
          position: {
            lat: Number(geoLocation[1]),
            lng: Number(geoLocation[2]),
          },
          radius: Number(r.company_hq_include_zip_code_distance),
          unit: r.company_hq_include_zip_code_unit || 'Miles',
        },
      },
    };
  }

  if (
    r.company_hq_exclude_zip_code_country &&
    r.company_hq_exclude_zip_code_distance
  ) {
    const geoLocation = r.company_hq_exclude_zip_code_country.split(':');
    request[QueryTarget.account] = {
      ...request[QueryTarget.account],
      ...{
        geoLocation: {
          position: { lat: geoLocation[1], lng: geoLocation[2] },
          radius: r.company_hq_exclude_zip_code_distance,
          unit: r.company_hq_exclude_zip_code_unit || 'Miles',
        },
      },
    };
  }

  if (r.include_linkedin_url_for_account) {
    const v = r.include_linkedin_url_for_account
      .trim()
      .split(FILTER_VALUE_SEPARATOR)
      .map((d: string) => cleanUrl(d))
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'linkedin',
      'any',
      'include',
      v,
      QueryTarget.account
    );
  }

  if (r.include_phone_numbers_for_account)
    request = addInclusionQuery(
      request,
      'phoneNumber',
      'any',
      'include',
      r.include_phone_numbers_for_account,
      QueryTarget.account
    );

  if (r.include_phone_numbers_for_contact)
    request = addInclusionQuery(
      request,
      'phoneNumber',
      'any',
      'include',
      r.include_phone_numbers_for_contact,
      QueryTarget.contact
    );

  if (r.include_company_ids) {
    const cleanValue = r.include_company_ids
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'id',
      'any',
      'include',
      cleanValue,
      QueryTarget.account
    );
  }

  if (r.exclude_company_ids) {
    const cleanValue = r.exclude_company_ids
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);

    request = addInclusionQuery(
      request,
      'id',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.account
    );
  }

  // Bulk Input Linkedin
  if (r.bulk_include_company_social) {
    const v = r.bulk_include_company_social.split(
      BULK_FILTER_VALUE_SEPARATOR
    )[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'include',
      v,
      QueryTarget.account,
      'companyLinkedin'
    );
  }
  if (r.bulk_exclude_company_social) {
    const v = r.bulk_exclude_company_social.split(
      BULK_FILTER_VALUE_SEPARATOR
    )[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'exclude',
      v,
      QueryTarget.account,
      'companyLinkedin'
    );
  }

  // Bulk Input Name
  if (r.bulk_include_company_name) {
    const v = r.bulk_include_company_name.split(BULK_FILTER_VALUE_SEPARATOR)[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'include',
      v,
      QueryTarget.account,
      'companyName'
    );
  }
  if (r.bulk_exclude_company_name) {
    const v = r.bulk_exclude_company_name.split(BULK_FILTER_VALUE_SEPARATOR)[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'exclude',
      v,
      QueryTarget.account,
      'companyName'
    );
  }

  // Bulk Input Domain
  if (r.bulk_include_company_domain) {
    const v = r.bulk_include_company_domain.split(
      BULK_FILTER_VALUE_SEPARATOR
    )[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'include',
      v,
      QueryTarget.account,
      'companyDomain'
    );
  }
  if (r.bulk_exclude_company_domain) {
    const v = r.bulk_exclude_company_domain.split(
      BULK_FILTER_VALUE_SEPARATOR
    )[0];
    request = addSearchListInclusionQuery(
      request,
      'searchList',
      'any',
      'exclude',
      v,
      QueryTarget.account,
      'companyDomain'
    );
  }

  if (r.include_industry)
    request = addInclusionQuery(
      request,
      'industries',
      'any',
      'include',
      r.include_industry,
      QueryTarget.account,
      'include_industry',
      r.exact_match
    );
  if (r.exclude_industry)
    request = addInclusionQuery(
      request,
      'industries',
      'any',
      'exclude',
      r.exclude_industry,
      QueryTarget.account,
      'exclude_industry',
      r.exact_match
    );

  if (r.include_technologies) {
    request = addInclusionQuery(
      request,
      'technologies',
      'any',
      'include',
      r.include_technologies,
      QueryTarget.account,
      'include_technologies',
      r.exact_match
    );
  }

  if (r.exclude_technologies) {
    request = addInclusionQuery(
      request,
      'technologies',
      'any',
      'exclude',
      r.exclude_technologies,
      QueryTarget.account,
      'exclude_technologies',
      r.exact_match
    );
  }

  if (r.all_of_technologies) {
    request = addInclusionQuery(
      request,
      'technologies',
      'all',
      'include',
      r.all_of_technologies,
      QueryTarget.account,
      'all_of_technologies',
      r.exact_match
    );
  }

  if (r.include_languages_for_account)
    request = addInclusionQuery(
      request,
      'language',
      'any',
      'include',
      r.include_languages_for_account,
      QueryTarget.account
    );

  if (r.exclude_languages_for_account)
    request = addInclusionQuery(
      request,
      'language',
      'any',
      'exclude',
      r.exclude_languages_for_account,
      QueryTarget.account
    );

  if (r.all_of_languages_for_account)
    request = addInclusionQuery(
      request,
      'language',
      'all',
      'include',
      r.all_of_languages_for_account,
      QueryTarget.account
    );

  if (r.include_sics)
    request = addInclusionQuery(
      request,
      'sicCode', // TODO
      'any',
      'include',
      r.include_sics,
      QueryTarget.account
    );

  if (r.exclude_sics)
    request = addInclusionQuery(
      request,
      'sicCode', // TODO
      'any',
      'exclude',
      r.exclude_sics,
      QueryTarget.account
    );

  if (r.number_of_languages_for_account) {
    const tempValue = r.number_of_languages_for_account.split(
      RANGE_FILTER_SEPARATOR
    );
    request[QueryTarget.account].language = {
      ...request[QueryTarget.account].language,
      ...{
        range: { start: tempValue[0], end: tempValue[1] },
      },
    };
  }

  if (r.employee_size)
    request = addRangeQuery(
      request,
      'employeeSize',
      QueryTarget.account,
      r.employee_size === 'All Ranges' ? 'ALL' : 'RANGE',
      'range',
      r.employee_size.replace('+', ''),
      '-'
    );

  if (r.employee_size_custom)
    request = addRangeQuery(
      request,
      'employeeSize',
      QueryTarget.account,
      'RANGE',
      'range',
      r.employee_size_custom
    );

  if (r.employee_size_availability)
    request = addRangeQuery(
      request,
      'employeeSize',
      QueryTarget.account,
      'NONE'
    );

  if (r.revenue)
    request = addRangeQuery(
      request,
      'revenue',
      QueryTarget.account,
      r.revenue === 'All Ranges' ? 'ALL' : 'RANGE',
      'range',
      mappingCompanyRevenue(r.revenue),
      '-'
    );

  if (r.annual_revenue_availability)
    request = addRangeQuery(request, 'revenue', QueryTarget.account, 'NONE');

  if (r.funding_type) {
    request[QueryTarget.account].funding = {
      ...request[QueryTarget.account].funding,
      ...{
        type: r.funding_type?.split(FILTER_VALUE_SEPARATOR),
      },
    };
  }

  if (r.all_funded_last_raised_at) {
    const tempValue = r.all_funded_last_raised_at.split(RANGE_FILTER_SEPARATOR);
    request[QueryTarget.account].funding = {
      ...request[QueryTarget.account].funding,
      ...{
        duration: {
          start: tempValue[0] ? Number(tempValue[0]) : undefined,
          end: tempValue[1] ? Number(tempValue[1]) : undefined,
        },
      },
    };
  }

  if (r.all_funded_latest_funding_amount) {
    const tempValue = r.all_funded_latest_funding_amount.split(
      RANGE_FILTER_SEPARATOR
    );
    request[QueryTarget.account].funding = {
      ...request[QueryTarget.account].funding,
      ...{
        lastAmount: {
          start: tempValue[0] ? Number(tempValue[0]) : undefined,
          end: tempValue[1] ? Number(tempValue[1]) : undefined,
        },
      },
    };
  }

  if (r.all_funded_total_funding_amount) {
    const tempValue = r.all_funded_total_funding_amount.split(
      RANGE_FILTER_SEPARATOR
    );
    request[QueryTarget.account].funding = {
      ...request[QueryTarget.account].funding,
      ...{
        totalAmount: {
          start: tempValue[0] ? Number(tempValue[0]) : undefined,
          end: tempValue[1] ? Number(tempValue[1]) : undefined,
        },
      },
    };
  }

  if (r.funding_availability) {
    request[QueryTarget.account].funding = {
      ...request[QueryTarget.account].funding,
      ...{
        notReceived: true,
      },
    };
  }

  if (r.year_founded)
    request = addSingleRangeQuery(
      request,
      'foundedYear',
      QueryTarget.account,
      r.year_founded
    );

  if (r.companies_include_keywords) {
    request = addInclusionWithSourceQuery(
      request,
      'keyword',
      'any',
      'include',
      QueryTarget.account,
      r.companies_include_keywords,
      r.companies_include_keywords_sources,
      r.companies_include_keywords_sources_search_match
    );
  }

  if (r.companies_exclude_keywords) {
    request = addInclusionWithSourceQuery(
      request,
      'keyword',
      'any',
      'exclude',
      QueryTarget.account,
      r.companies_exclude_keywords,
      r.companies_exclude_keywords_sources,
      r.companies_exclude_keywords_sources_search_match
    );
  }

  if (r.companies_all_of_keywords) {
    request = addInclusionWithSourceQuery(
      request,
      'keyword',
      'all',
      'include',
      QueryTarget.account,
      r.companies_all_of_keywords,
      r.companies_all_of_keywords_sources,
      r.companies_all_of_keywords_sources_search_match
    );
  }

  if (r.include_products_and_services) {
    request = addInclusionQuery(
      request,
      'productAndServices',
      'any',
      'include',
      r.include_products_and_services,
      QueryTarget.account,
      'include_products_and_services',
      r.exact_match,
      SearchMatchType.WORD
    );
  }

  if (r.exclude_products_and_services) {
    request = addInclusionQuery(
      request,
      'productAndServices',
      'any',
      'exclude',
      r.exclude_products_and_services,
      QueryTarget.account,
      'exclude_products_and_services',
      r.exact_match,
      SearchMatchType.WORD
    );
  }

  if (r.all_of_products_and_services) {
    request = addInclusionQuery(
      request,
      'productAndServices',
      'all',
      'include',
      r.all_of_products_and_services,
      QueryTarget.account,
      'all_of_products_and_services',
      r.exact_match,
      SearchMatchType.WORD
    );
  }

  if (r.company_hq_include_location_region) {
    const v = calcSelectedLocations(
      r.company_hq_include_location_region,
      undefined
    );

    request = addInclusionQuery(
      request,
      'location',
      'any',
      'include',
      v,
      QueryTarget.account
    );
  }

  if (r.company_hq_include_location_cities) {
    request = addInclusionQuery(
      request,
      'locations',
      'any',
      'include',
      r.company_hq_include_location_cities,
      QueryTarget.account,
      'company_hq_include_location_cities',
      r.exact_match
    );
  }

  if (r.company_hq_exclude_location_region) {
    const v = calcSelectedLocations(
      r.company_hq_exclude_location_region,
      r.undefined
    );

    request = addInclusionQuery(
      request,
      'location',
      'any',
      'exclude',
      v,
      QueryTarget.account
    );
  }

  if (r.company_hq_exclude_location_cities) {
    request = addInclusionQuery(
      request,
      'locations',
      'any',
      'exclude',
      r.company_hq_exclude_location_cities,
      QueryTarget.account,
      'company_hq_exclude_location_cities',
      r.exact_match
    );
  }

  if (r.contact_include_location_region) {
    const v = calcSelectedLocations(
      r.contact_include_location_region,
      undefined
    );

    request = addInclusionQuery(
      request,
      'location',
      'any',
      'include',
      v,
      QueryTarget.contact
    );
  }

  if (r.contact_include_location_cities) {
    request = addInclusionQuery(
      request,
      'locations',
      'any',
      'include',
      r.contact_include_location_cities,
      QueryTarget.contact,
      'contact_include_location_cities',
      r.exact_match
    );
  }

  if (r.contact_exclude_location_region) {
    const v = calcSelectedLocations(
      r.contact_exclude_location_region,
      undefined
    );

    request = addInclusionQuery(
      request,
      'location',
      'any',
      'exclude',
      v,
      QueryTarget.contact
    );
  }

  if (r.contact_exclude_location_cities) {
    request = addInclusionQuery(
      request,
      'locations',
      'any',
      'exclude',
      r.contact_exclude_location_cities,
      QueryTarget.contact,
      'contact_exclude_location_cities',
      r.exact_match
    );
  }

  if (r.social_media_company_include_social_media) {
    request = addInclusionQuery(
      request,
      'socialMedia',
      'any',
      'include',
      r.social_media_company_include_social_media,
      QueryTarget.account
    );
  }

  if (r.social_media_company_exclude_social_media) {
    request = addInclusionQuery(
      request,
      'socialMedia',
      'any',
      'exclude',
      r.social_media_company_exclude_social_media,
      QueryTarget.account
    );
  }

  if (r.social_media_company_all_of_social_media) {
    request = addInclusionQuery(
      request,
      'socialMedia',
      'all',
      'include',
      r.social_media_company_all_of_social_media,
      QueryTarget.account
    );
  }

  if (r.company_linkedin_followers_predefined) {
    request = setNestedRange(
      request,
      QueryTarget.account,
      'linkedin',
      'followers',
      {
        type: 'RANGE',
        range: mappingFollowerRanges(r.company_linkedin_followers_predefined),
      }
    );
  }

  if (r.company_linkedin_followers_custom) {
    request = setNestedRange(
      request,
      QueryTarget.account,
      'linkedin',
      'followers',
      {
        type: 'RANGE',
        range: mappingCustomRange(r.company_linkedin_followers_custom),
      }
    );
  }

  if (r.company_linkedin_followers_availability) {
    request = setNestedRange(
      request,
      QueryTarget.account,
      'linkedin',
      'followers',
      { type: 'NONE' }
    );
  }

  // ----
  if (r.social_media_contacts_include_social_media) {
    request = addInclusionQuery(
      request,
      'socialMedia',
      'any',
      'include',
      r.social_media_contacts_include_social_media,
      QueryTarget.contact
    );
  }

  if (r.social_media_contacts_exclude_social_media) {
    request = addInclusionQuery(
      request,
      'socialMedia',
      'any',
      'exclude',
      r.social_media_contacts_exclude_social_media,
      QueryTarget.contact
    );
  }

  if (r.social_media_contacts_all_of_social_media) {
    request = addInclusionQuery(
      request,
      'socialMedia',
      'all',
      'include',
      r.social_media_contacts_all_of_social_media,
      QueryTarget.contact
    );
  }

  if (r.record_ownership_account_include) {
    const selected_user_ids = r.record_ownership_account_include
      .split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'user',
      'any',
      'include',
      selected_user_ids,
      QueryTarget.account
    );
  }

  if (r.record_ownership_account_exclude) {
    const selected_user_ids = r.record_ownership_account_exclude
      .split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'user',
      'any',
      'exclude',
      selected_user_ids,
      QueryTarget.account
    );
  }

  if (r.record_ownership_contact_include) {
    const selected_user_ids = r.record_ownership_contact_include
      .split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'user',
      'any',
      'include',
      selected_user_ids,
      QueryTarget.contact
    );
  }

  if (r.record_ownership_contact_exclude) {
    const selected_user_ids = r.record_ownership_contact_exclude
      .split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'user',
      'any',
      'exclude',
      selected_user_ids,
      QueryTarget.contact
    );
  }

  if (r.companies_include_list || r.labels_account_include) {
    const selected_list_ids = r.companies_include_list
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    const selected_label_ids = r.labels_account_include
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);

    request = addInclusionQuery(
      request,
      'label',
      'any',
      'include',
      [selected_list_ids, selected_label_ids]
        .filter((i) => !!i)
        .join(FILTER_VALUE_SEPARATOR),
      QueryTarget.account
    );
  }

  if (r.companies_exclude_list || r.labels_account_exclude) {
    const selected_list_ids = r.companies_exclude_list
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    const selected_label_ids = r.labels_account_exclude
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'label',
      'any',
      'exclude',
      [selected_list_ids, selected_label_ids]
        .filter((i) => !!i)
        .join(FILTER_VALUE_SEPARATOR),
      QueryTarget.account
    );
  }

  if (r.companies_all_of_list || r.companies_all_of_labels) {
    const selected_list_ids = r.companies_all_of_list
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    const selected_label_ids = r.companies_all_of_labels
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'label',
      'all',
      'include',
      [selected_list_ids, selected_label_ids]
        .filter((i) => !!i)
        .join(FILTER_VALUE_SEPARATOR),
      QueryTarget.account
    );
  }

  if (r.people_include_list || r.labels_contact_include) {
    const selected_list_ids = r.people_include_list
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    const selected_label_ids = r.labels_contact_include
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'label',
      'any',
      'include',
      [selected_list_ids, selected_label_ids]
        .filter((i) => !!i)
        .join(FILTER_VALUE_SEPARATOR),
      QueryTarget.contact
    );
  }

  if (r.people_exclude_list || r.labels_contact_exclude) {
    const selected_list_ids = r.people_exclude_list
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    const selected_label_ids = r.labels_contact_exclude
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'label',
      'any',
      'exclude',
      [selected_list_ids, selected_label_ids]
        .filter((i) => !!i)
        .join(FILTER_VALUE_SEPARATOR),
      QueryTarget.contact
    );
  }

  if (r.people_all_of_list || r.contacts_all_of_labels) {
    const selected_list_ids = r.people_all_of_list
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    const selected_label_ids = r.contacts_all_of_labels
      ?.split(FILTER_VALUE_SEPARATOR)
      .map((l: string) => parseNameIdValue(l).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'label',
      'all',
      'include',
      [selected_list_ids, selected_label_ids]
        .filter((i) => !!i)
        .join(FILTER_VALUE_SEPARATOR),
      QueryTarget.contact
    );
  }

  if (r.number_of_retail_locations)
    request = addRangeQuery(
      request,
      'retailSize',
      QueryTarget.account,
      'RANGE',
      'range',
      r.number_of_retail_locations
    );

  if (r.employee_by_department) {
    const selectedFunctions = addEmployeeMetric(r.employee_by_department);

    if (selectedFunctions.length) {
      request.account.metrics = {
        ...request.account.metrics,
        ...{
          employee: request.account.metrics?.employee
            ? [...request.account.metrics.employee, ...selectedFunctions]
            : selectedFunctions,
        },
      };
    }
  }

  if (r.headcount_growth) {
    const selectedFunctions = addGrowthMetric(r.headcount_growth);
    if (selectedFunctions.length) {
      request.account.metrics = {
        ...request.account.metrics,
        ...{
          growth: request.account.metrics?.growth
            ? [...request.account.metrics.growth, ...selectedFunctions]
            : selectedFunctions,
        },
      };
    }
  }

  if (r.modality) request.modality = r.modality;

  if (r.companies_exports_request_include) {
    const cleanValue = r.companies_exports_request_include
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'export',
      'any',
      'include',
      cleanValue,
      QueryTarget.account
    );
  }
  if (r.companies_enrichment_request_include) {
    const cleanValue = r.companies_enrichment_request_include
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'enrichment',
      'any',
      'include',
      cleanValue,
      QueryTarget.account
    );
  }
  if (r.companies_enrichment_request_exclude) {
    const cleanValue = r.companies_enrichment_request_exclude
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'enrichment',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.account
    );
  }
  if (r.companies_enrichment_request_all_of) {
    const cleanValue = r.companies_enrichment_request_all_of
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'enrichment',
      'all',
      'include',
      cleanValue,
      QueryTarget.account
    );
  }

  if (r.companies_exports_request_exclude) {
    const cleanValue = r.companies_exports_request_exclude
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'export',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.account
    );
  }

  if (r.companies_exports_request_all_of) {
    const cleanValue = r.companies_exports_request_all_of
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'export',
      'all',
      'include',
      cleanValue,
      QueryTarget.account
    );
  }

  if (r.people_exports_request_include) {
    const cleanValue = r.people_exports_request_include
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'export',
      'any',
      'include',
      cleanValue,
      QueryTarget.contact
    );
  }
  if (r.people_enrichment_request_include) {
    const cleanValue = r.people_enrichment_request_include
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'enrichment',
      'any',
      'include',
      cleanValue,
      QueryTarget.contact
    );
  }
  if (r.people_enrichment_request_exclude) {
    const cleanValue = r.people_enrichment_request_exclude
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'enrichment',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.contact
    );
  }
  if (r.people_enrichment_request_all_of) {
    const cleanValue = r.people_enrichment_request_all_of
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'enrichment',
      'all',
      'include',
      cleanValue,
      QueryTarget.contact
    );
  }

  if (r.people_exports_request_exclude) {
    const cleanValue = r.people_exports_request_exclude
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'export',
      'any',
      'exclude',
      cleanValue,
      QueryTarget.contact
    );
  }

  if (r.people_exports_request_all_of) {
    const cleanValue = r.people_exports_request_all_of
      .split(FILTER_VALUE_SEPARATOR)
      .map((i: string) => parseNameIdValue(i).id)
      .join(FILTER_VALUE_SEPARATOR);
    request = addInclusionQuery(
      request,
      'export',
      'all',
      'include',
      cleanValue,
      QueryTarget.contact
    );
  }

  return request;
}
